import { useLayoutEffect, useRef, type ComponentProps, type Ref } from 'react';
import { Toast as BaseToast } from '@base-ui/react/toast';
import { ButtonControl, TooltipProvider } from './ButtonControl';

import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Icon } from './Icon';

export type ToastTone = 'success' | 'error' | 'warning';

export const TOAST_TIMEOUT_MS = 5000;

export const toastManager = BaseToast.createToastManager()

export function toastError(message: string): void {
  toastManager.add({ description: message, type: 'error', priority: 'high' })
}

export function toastInfo(message: string): void {
  toastManager.add({ description: message, type: 'info' })
}

export function toastSuccess(message: string): void {
  toastManager.add({ description: message, type: 'success' })
}

export function toastWarning(message: string): string {
  return toastManager.add({ description: message, type: 'warning', priority: 'high' })
}

export function dismissToast(id: string): void {
  toastManager.close(id)
}

const TOAST_ICON_NAME: Record<ToastTone, string> = {
  success: 'i-check',
  error: 'i-x',
  warning: 'i-bolt',
};

export interface ToastProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  tone: ToastTone;
  children: ReactNode;
}

export function Toast(
  { ref, tone, children, className, ...rest }: ToastProps & { ref?: Ref<HTMLDivElement> },
) {
  return (
    <div ref={ref} className={cx('toast', `is-${tone}`, className)} {...rest}>
      <span className="toast-icon">
        <Icon
          name={TOAST_ICON_NAME[tone]}
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </span>
      {children}
    </div>
  );
}

export type ToastCloseProps = ButtonHTMLAttributes<HTMLButtonElement>;

export function ToastClose(
  { ref, className, type = 'button', 'aria-label': ariaLabel = "Schließen", ...rest }: ToastCloseProps & { ref?: Ref<HTMLButtonElement> },
) {
  return (
    <ButtonControl ref={ref} type={type} aria-label={ariaLabel} className={cx('toast-close', className)} {...rest}>
      <Icon name="i-x" />
    </ButtonControl>
  );
}

export type ToastViewportProps = ComponentProps<'div'>;

export function ToastViewport({ className, ...props }: ToastViewportProps) {
  return <div className={cx('toast-viewport', className)} {...props} />;
}

function toneOf(type: string | undefined): ToastTone {
  return type === 'success' || type === 'error' ? type : 'warning'
}

function ToastList() {
  const { toasts } = BaseToast.useToastManager()
  const rail = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const header = document.querySelector<HTMLElement>('[data-main-layout] > .bar')
    const align = () => {
      const pill = header?.querySelector<HTMLElement>('.pill')
      rail.current?.style.setProperty('top', `${pill?.getBoundingClientRect().top ?? 10}px`)
    }
    align()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(align)
    if (header) observer?.observe(header)
    window.addEventListener('resize', align)
    return () => { observer?.disconnect(); window.removeEventListener('resize', align) }
  }, [toasts.length])
  return <ToastViewport ref={rail}>{toasts.map((toast) => (
    <BaseToast.Root
      key={toast.id}
      toast={toast}
    >
      <Toast tone={toneOf(toast.type)}>
        <span className="toast-message">
          {toast.title ? <BaseToast.Title>{toast.title}</BaseToast.Title> : null}
          {toast.description ? <BaseToast.Description>{toast.description}</BaseToast.Description> : null}
        </span>
        <BaseToast.Close render={<ToastClose />} />
      </Toast>
    </BaseToast.Root>
  ))}</ToastViewport>
}

export function Toaster({ children }: { children: ReactNode }) {
  return (
    <TooltipProvider><BaseToast.Provider toastManager={toastManager} timeout={TOAST_TIMEOUT_MS}>
      {children}
      <BaseToast.Portal>
        <BaseToast.Viewport>
          <ToastList />
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </BaseToast.Provider></TooltipProvider>
  )
}
